import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { dashboardApi } from '../../services/iso20022Api';
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Download,
  RefreshCw,
  ExternalLink,
  Calendar,
  User,
  Building2,
  Shield,
  FileCode,
  Link as LinkIcon
} from 'lucide-react';
import { formatDate } from '../../utils/helpers';
import XmlViewerModal from '../../components/iso20022/XmlViewerModal';

/**
 * RTP Status Detail Page (pain.014)
 * Shows detailed information about a single RTP status/response message
 * 
 * Features:
 * - Complete message details with all fields
 * - Related messages (pain.013, pacs.008, pacs.002)
 * - Party information display
 * - Signature validation status
 * - XML viewer modal
 * - Download XML functionality
 */
const RtpStatusDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [status, setStatus] = useState(null);
  const [relatedMessages, setRelatedMessages] = useState(null);
  const [loading, setLoading] = useState(true);
  const [xmlModalOpen, setXmlModalOpen] = useState(false);

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);

    try {
      const [statusData, relatedData] = await Promise.all([
        dashboardApi.getRtpStatusTransactionById(id),
        dashboardApi.getRtpStatusRelatedMessages(id)
      ]);

      console.log('[Load RTP Status Detail] Transaction:', statusData);
      console.log('[Load Related Messages]:', relatedData);

      setStatus(statusData);
      setRelatedMessages(relatedData);
    } catch (err) {
      console.error('[Load RTP Status Detail] Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (sts) => {
    if (sts?.startsWith('ACT') || sts?.startsWith('ACS')) return CheckCircle2;
    if (sts === 'RJCT') return XCircle;
    if (sts === 'PDNG') return Clock;
    return RefreshCw;
  };

  const getStatusBadge = (sts) => {
    const badges = {
      'ACTC': 'bg-success-100 text-success-800 border-success-200',
      'ACSC': 'bg-teal-100 text-teal-800 border-teal-200',
      'RJCT': 'bg-danger-100 text-danger-800 border-danger-200',
      'PDNG': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'PART': 'bg-orange-100 text-orange-800 border-orange-200',
      'ACCP': 'bg-success-100 text-success-800 border-success-200',
      'ACSP': 'bg-blue-100 text-blue-800 border-blue-200',
      'ACWC': 'bg-purple-100 text-purple-800 border-purple-200',
      'RCVD': 'bg-gray-100 text-gray-800 border-gray-200'
    };

    return badges[sts] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const handleDownloadXml = () => {
    if (!status?.rawXml) {
      alert('Raw XML not available');
      return;
    }

    const blob = new Blob([status.rawXml], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rtp-status-${status.msgId || status.id}.xml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center space-y-4">
          <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
          <p className="text-gray-600">Loading RTP Status details...</p>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-900 font-medium mb-2">RTP Status message not found</p>
          <Link to="/iso20022/rtp-statuses" className="text-primary-600 hover:text-primary-700">
            ← Back to RTP Statuses
          </Link>
        </div>
      </div>
    );
  }

  const StatusIcon = getStatusIcon(status.grpSts || status.txSts);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/iso20022/rtp-statuses')}
            className="btn-secondary flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              RTP Status #{status.id}
            </h1>
            <p className="text-gray-600 mt-1">pain.014 message details</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {status.rawXml && (
            <>
              <button onClick={handleDownloadXml} className="btn-secondary flex items-center space-x-2">
                <Download className="w-4 h-4" />
                <span>Download XML</span>
              </button>
              <button onClick={() => setXmlModalOpen(true)} className="btn-secondary flex items-center space-x-2">
                <FileCode className="w-4 h-4" />
                <span>View XML</span>
              </button>
            </>
          )}
        </div>
      </div>

      {/* Status Badge */}
      <div className="card">
        <div className="card-body">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                status.isAccepted ? 'bg-success-100' : status.isRejected ? 'bg-danger-100' : 'bg-yellow-100'
              }`}>
                <StatusIcon className={`w-6 h-6 ${
                  status.isAccepted ? 'text-success-600' : status.isRejected ? 'text-danger-600' : 'text-yellow-600'
                }`} />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{status.grpStsLabel || status.txStsLabel}</h2>
                <p className="text-gray-600 mt-1">
                  {status.directionLabel} · {status.statusLabel}
                </p>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-lg text-sm font-medium border ${getStatusBadge(status.grpSts || status.txSts)}`}>
              {status.grpSts || status.txSts}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Message Identifiers */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Message Identifiers</h2>
          </div>
          <div className="card-body">
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Message ID</dt>
                <dd className="mt-1 text-sm font-mono text-gray-900">{status.msgId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center space-x-1">
                  <span>Original Message ID</span>
                  <LinkIcon className="w-3 h-3 text-blue-500" title="Links to pain.013" />
                </dt>
                <dd className="mt-1 text-sm font-mono text-blue-600 font-semibold">{status.orgnlMsgId}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Original Message Name ID</dt>
                <dd className="mt-1 text-sm text-gray-900">{status.orgnlMsgNmId || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 flex items-center space-x-1">
                  <span>Original End-to-End ID</span>
                  <LinkIcon className="w-3 h-3 text-blue-500" title="Links to pacs.008" />
                </dt>
                <dd className="mt-1 text-sm font-mono text-blue-600 font-semibold">{status.orgnlEndToEndId || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Original Payment Info ID</dt>
                <dd className="mt-1 text-sm font-mono text-gray-900">{status.orgnlPmtInfId || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Original Instruction ID</dt>
                <dd className="mt-1 text-sm font-mono text-gray-900">{status.orgnlInstrId || 'N/A'}</dd>
              </div>
              {status.stsId && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status ID</dt>
                  <dd className="mt-1 text-sm font-mono text-gray-900">{status.stsId}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Status Details */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900">Status Details</h2>
          </div>
          <div className="card-body">
            <dl className="grid grid-cols-1 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Group Status</dt>
                <dd className="mt-1 text-sm text-gray-900">{status.grpStsLabel}</dd>
              </div>
              {status.grpStsRsn && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Group Status Reason</dt>
                  <dd className="mt-1 text-sm text-gray-900">{status.grpStsRsn}</dd>
                </div>
              )}
              {status.pmtInfSts && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Info Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">{status.pmtInfSts}</dd>
                </div>
              )}
              {status.pmtInfStsRsn && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Info Reason</dt>
                  <dd className="mt-1 text-sm text-gray-900">{status.pmtInfStsRsn}</dd>
                </div>
              )}
              {status.txSts && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Transaction Status</dt>
                  <dd className="mt-1 text-sm text-gray-900">{status.txStsLabel}</dd>
                </div>
              )}
              {status.txStsRsn && (
                <div>
                  <dt className="text-sm font-medium text-gray-500">Transaction Reason</dt>
                  <dd className="mt-1 text-sm text-gray-900">{status.txStsRsn}</dd>
                </div>
              )}
            </dl>
          </div>
        </div>
      </div>

      {/* Party Information */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <User className="w-5 h-5" />
            <span>Party Information</span>
          </h2>
        </div>
        <div className="card-body">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Initiating Party Name</dt>
              <dd className="mt-1 text-sm text-gray-900">{status.initgPtyName || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Initiating Party ID</dt>
              <dd className="mt-1 text-sm font-mono text-gray-900">{status.initgPtyId || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Debtor Agent (BICFI)</dt>
              <dd className="mt-1 text-sm font-mono text-gray-900">{status.dbtrAgtBicfi || 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Creditor Agent (BICFI)</dt>
              <dd className="mt-1 text-sm font-mono text-gray-900">{status.cdtrAgtBicfi || 'N/A'}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Transaction Summary */}
      {status.transactionSummary && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <Building2 className="w-5 h-5" />
              <span>Transaction Summary</span>
            </h2>
          </div>
          <div className="card-body">
            <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Number of Transactions</dt>
                <dd className="mt-1 text-sm text-gray-900">{status.orgnlNbOfTxs || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Control Sum</dt>
                <dd className="mt-1 text-sm text-gray-900">{status.orgnlCtrlSum || 'N/A'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Summary</dt>
                <dd className="mt-1 text-sm text-gray-900">{status.transactionSummary}</dd>
              </div>
            </dl>
          </div>
        </div>
      )}

      {/* Timestamps */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Calendar className="w-5 h-5" />
            <span>Timestamps</span>
          </h2>
        </div>
        <div className="card-body">
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Debtor Decision Time</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {status.dbtrDcsnDtTm ? formatDate(status.dbtrDcsnDtTm, 'PPpp') : 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Acceptance Time</dt>
              <dd className="mt-1 text-sm text-gray-900">
                {status.accptcDtTm ? formatDate(status.accptcDtTm, 'PPpp') : 'N/A'}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Created At</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(status.createdAt, 'PPpp')}</dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-gray-500">Updated At</dt>
              <dd className="mt-1 text-sm text-gray-900">{formatDate(status.updatedAt, 'PPpp')}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Signature Information */}
      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
            <Shield className="w-5 h-5" />
            <span>Signature Information</span>
          </h2>
        </div>
        <div className="card-body">
          <dl className="grid grid-cols-1 gap-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Signature Valid</dt>
              <dd className="mt-1">
                <span className={`inline-flex items-center ${status.signatureOk ? 'text-success-600' : 'text-danger-600'}`}>
                  {status.signatureOk ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 mr-1" />
                      Valid
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 mr-1" />
                      Invalid
                    </>
                  )}
                </span>
              </dd>
            </div>
            {status.signatureDetails && (
              <div>
                <dt className="text-sm font-medium text-gray-500">Signature Details</dt>
                <dd className="mt-1 text-sm text-gray-900 font-mono">{status.signatureDetails}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>

      {/* Related Messages */}
      {relatedMessages && (
        <div className="card">
          <div className="card-header">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center space-x-2">
              <LinkIcon className="w-5 h-5" />
              <span>Related Messages</span>
            </h2>
          </div>
          <div className="card-body space-y-4">
            {/* Original RTP Request */}
            {relatedMessages.rtpRequest && (
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-900 flex items-center space-x-2">
                      <FileCode className="w-4 h-4" />
                      <span>Original RTP Request (pain.013)</span>
                    </p>
                    <p className="text-xs text-blue-700 mt-1 font-mono">
                      {relatedMessages.rtpRequest.msgId}
                    </p>
                    {relatedMessages.rtpRequest.amountCcy && (
                      <p className="text-xs text-blue-600 mt-1">
                        Amount: {relatedMessages.rtpRequest.amountCcy}
                      </p>
                    )}
                  </div>
                  <Link
                    to={`/iso20022/rtp/${relatedMessages.rtpRequest.id}`}
                    className="btn-secondary text-sm flex items-center space-x-1"
                  >
                    <span>View Request</span>
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            )}

            {/* Payment */}
            {relatedMessages.payment && (
              <div className="p-4 bg-success-50 rounded-lg border border-success-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-success-900 flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Payment (pacs.008)</span>
                    </p>
                    <p className="text-xs text-success-700 mt-1 font-mono">
                      {relatedMessages.payment.grpMsgId}
                    </p>
                    <p className="text-xs text-success-600 mt-1">
                      Amount: {relatedMessages.payment.amountCcy}
                    </p>
                  </div>
                  <Link
                    to={`/iso20022/transactions/${relatedMessages.payment.id}`}
                    className="btn-secondary text-sm flex items-center space-x-1"
                  >
                    <span>View Payment</span>
                    <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </div>
            )}

            {/* Payment Statuses */}
            {relatedMessages.paymentStatuses && relatedMessages.paymentStatuses.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-700">Payment Status Updates (pacs.002)</p>
                {relatedMessages.paymentStatuses.map((pStatus, index) => (
                  <div key={index} className="p-3 bg-gray-50 rounded border border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-mono text-gray-900">{pStatus.orglMsgId}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          Status: {pStatus.txSts} · {formatDate(pStatus.createdAt, 'PPpp')}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {!relatedMessages.rtpRequest && !relatedMessages.payment && (
              <p className="text-sm text-gray-500 italic text-center py-4">No related messages found</p>
            )}
          </div>
        </div>
      )}

      {/* XML Viewer Modal */}
      <XmlViewerModal
        isOpen={xmlModalOpen}
        onClose={() => setXmlModalOpen(false)}
        xml={status?.rawXml}
        title={`RTP Status: ${status?.msgId}`}
      />
    </div>
  );
};

export default RtpStatusDetail;
